import gsap from 'gsap';
import React, { createRef, forwardRef, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '../Icon';
import { InputContainer, InputElement, InputWrapper } from './Input';
const NumberContainer = styled(InputContainer) `
  display: flex;
`;
const Fields = styled.div `
  --number-current-opacity: 0;
  --number-current-y: 0;
  --number-current-blur: 0px;
  --number-update-opacity: 0;
  --number-update-y: 0;
  --number-update-blur: 8px;
  position: relative;
  flex-grow: 1;
`;
const Current = styled.div `
  pointer-events: none;
  z-index: 1;
  position: absolute;
  left: 0;
  top: 0;
  color: inherit;
  user-select: none;
  line-height: var(--input-line-height);
  opacity: var(--number-current-opacity);
  transform: translateY(var(--number-current-y)) translateZ(0);
  transition: color 0.2s;

  ${({ $isPlaceholder }) => $isPlaceholder &&
    css `
      color: var(--input-placeholder-color);
    `}
`;
const Update = styled(Current) `
  opacity: var(--number-update-opacity);
  transform: translateY(var(--number-update-y)) translateZ(0);
`;
const Actions = styled.div `
  margin: -5px -5px -5px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const Up = styled(Icon.ChevronUp) `
  --icon-size: 12px;

  path {
    transition:
      stroke 0.2s,
      fill 0.2s;
  }

  ${({ $isSmall }) => $isSmall &&
    css `
      --icon-size: 10px;
    `}

  ${({ $isDisabled }) => !$isDisabled &&
    css `
      --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
      cursor: pointer;

      &:hover {
        --icon-color: ${({ theme }) => theme.secondary.background};
      }
    `}

  ${({ $isDisabled }) => $isDisabled &&
    css `
      --icon-color: ${({ theme }) => theme.secondary.blendToBackground(200)};
      cursor: not-allowed;
    `}
`;
const Down = styled(Icon.ChevronDown) `
  --icon-size: 12px;

  path {
    transition:
      stroke 0.2s,
      fill 0.2s;
  }

  ${({ $isSmall }) => $isSmall &&
    css `
      --icon-size: 10px;
    `}

  ${({ $isDisabled }) => !$isDisabled &&
    css `
      --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
      cursor: pointer;

      &:hover {
        --icon-color: ${({ theme }) => theme.secondary.background};
      }
    `}

  ${({ $isDisabled }) => $isDisabled &&
    css `
      --icon-color: ${({ theme }) => theme.secondary.blendToBackground(200)};
      cursor: not-allowed;
    `}
`;
export const NumberInput = forwardRef((props, ref) => {
    const [isHoveredState, setIsHoveredState] = useState(props.isHovered ?? false);
    const [isFocussedState, setIsFocussedState] = useState(props.isFocussed ?? false);
    const [isFilledState, setIsFilledState] = useState(props.isFilled ?? false);
    const onChange = props.onChange ??
        (() => {
            /* NOOP */
        });
    const onFocus = props.onFocus ??
        (() => {
            /* NOOP */
        });
    const onBlur = props.onBlur ??
        (() => {
            /* NOOP */
        });
    const onKeyDown = props.onKeyDown ??
        (() => {
            /* NOOP */
        });
    const inputRef = createRef();
    const fieldsRef = createRef();
    const currentRef = createRef();
    const updateRef = createRef();
    const isError = useMemo(() => {
        if (typeof props.value === 'undefined') {
            return false;
        }
        const value = props.value.replaceAll(',', '');
        if (value.length < 1) {
            return false;
        }
        if ((typeof props.min === 'number' && Number(value) < props.min) ||
            (typeof props.max === 'number' && Number(value) > props.max)) {
            return true;
        }
        return false;
    }, [props.value, props.min, props.max]);
    const styleProps = {
        $isHovered: props.isHovered ?? isHoveredState,
        $isFocussed: props.isFocussed ?? isFocussedState,
        $isFilled: props.isFilled ?? isFilledState,
        $isDisabled: props.isDisabled ?? false,
        $isPressed: props.isPressed ?? false,
        $hasError: isError || (props.hasError ?? false),
        $size: props.size ?? 'default',
        $variant: props.variant ?? 'default',
    };
    const handleClick = () => {
        if (props.isDisabled) {
            return;
        }
        if (props.onClick) {
            props.onClick();
        }
        setIsFocussedState(true);
    };
    const formatNumber = (value) => {
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };
    const handleValueChange = (value) => {
        const newValue = value.replace(/,/g, '');
        if (newValue.length && !/^\d+$/.test(newValue)) {
            return;
        }
        onChange(formatNumber(newValue));
    };
    const handleStepChange = (manipulate) => {
        const step = (props.step ?? 1) * manipulate;
        const currentValue = props.value ? props.value : props.placeholder ? props.placeholder : '0';
        let nextNumValue = Number(currentValue.replaceAll(',', '')) + step;
        if (typeof props.min === 'number') {
            nextNumValue = Math.max(props.min, nextNumValue);
        }
        if (typeof props.max === 'number') {
            nextNumValue = Math.min(props.max, nextNumValue);
        }
        const newValue = formatNumber(nextNumValue.toString());
        const input = inputRef.current;
        const fields = fieldsRef.current;
        const current = currentRef.current;
        const update = updateRef.current;
        if (input && fields && current && update) {
            gsap.fromTo(fields, {
                '--number-current-opacity': '1',
                '--number-update-y': `${16 * manipulate}px`,
            }, {
                onStart: () => {
                    current.textContent = currentValue;
                    input.style.opacity = '0';
                    update.textContent = newValue;
                },
                '--number-current-opacity': '0',
                '--number-current-y': `${16 * -manipulate}px`,
                '--number-update-opacity': '1',
                '--number-update-y': '0px',
                duration: 0.15,
                clearProps: true,
                onComplete: () => {
                    handleValueChange(newValue);
                    input.style.opacity = '1';
                },
            });
        }
    };
    const handleUpClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (props.isDisabled) {
            return;
        }
        handleStepChange(1);
    };
    const handleDownClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (props.isDisabled) {
            return;
        }
        handleStepChange(-1);
    };
    const handleHoverChange = (value) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onHoverChange) {
            props.onHoverChange(value);
        }
        setIsHoveredState(value);
    };
    const handleFocusChange = (value) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onFocusChange) {
            props.onFocusChange(value);
        }
        setIsFocussedState(value);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && props.autoBlur) {
            inputRef.current?.blur();
        }
    };
    useEffect(() => {
        if (props.isFocussed ?? isFocussedState) {
            inputRef.current?.focus();
        }
        else {
            inputRef.current?.blur();
        }
    }, [isFocussedState, props.isFocussed]);
    useEffect(() => {
        if (typeof props.value === 'undefined') {
            setIsFilledState(false);
            return;
        }
        setIsFilledState(props.value.length > 0);
    }, [props.value]);
    return (React.createElement(InputWrapper, { ref: ref, style: props.style, className: props.className, onMouseEnter: () => handleHoverChange(true), onMouseMove: () => handleHoverChange(true), onMouseLeave: () => handleHoverChange(false), onClick: handleClick, "data-error": typeof props.hasError === 'string' ? props.hasError : undefined, ...styleProps },
        React.createElement(NumberContainer, { ...styleProps },
            React.createElement(Fields, { ref: fieldsRef },
                React.createElement(InputElement, { onChange: (e) => handleValueChange(e.target.value), onFocus: (e) => {
                        handleFocusChange(true);
                        onFocus(e);
                    }, onBlur: (e) => {
                        handleFocusChange(false);
                        onBlur(e);
                    }, onKeyDown: onKeyDown, onKeyPress: handleKeyPress, autoFocus: props.autoFocus, ref: inputRef, disabled: props.isDisabled ?? false, value: props.value, type: "text", inputMode: "numeric", pattern: "^[0-9,]*$", readOnly: props.readOnly ?? false, maxLength: props.maxLength, placeholder: props.placeholder }),
                React.createElement(Current, { ref: currentRef, "$isPlaceholder": !props.value }),
                React.createElement(Update, { ref: updateRef })),
            React.createElement(Actions, { ...styleProps },
                React.createElement(Up, { "$isDisabled": !!((props.value || props.placeholder) &&
                        typeof props.max === 'number' &&
                        Number((props.value || props.placeholder || '0').replaceAll(',', '')) >= props.max), "$isSmall": props.size === 'small', onClick: handleUpClick }),
                React.createElement(Down, { "$isDisabled": !!((props.value || props.placeholder) &&
                        typeof props.min === 'number' &&
                        Number((props.value || props.placeholder || '0').replaceAll(',', '')) <= props.min), "$isSmall": props.size === 'small', onClick: handleDownClick }))),
        props.children));
});
//# sourceMappingURL=Number.js.map